import { FC, useState } from "react";
import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import { ModalHeader } from "src/domains/shared/components/ModalHeader";
import { useAuth } from "src/domains/shared/hooks/user";
import { COLOR_CONSTANTS } from "src/domains/shared/constants/styles";
import { BranchFade } from "src/domains/shared/components/StateTransition";
import type { LineGlyphName } from "src/domains/shared/components/StateTransition";
import { MYPAGE_NAV_BG, MYPAGE_SURFACE_BG } from "./myPageStyles";
import { AccountInfo } from "./AccountInfo";
import { UserManagement } from "./UserManagement";
import { ProjectHistory } from "./ProjectHistory";
import { SideMenuItem } from "./components/SideMenuItem";
import { LogoutConfirmDialog } from "./components/LogoutConfirmDialog";

interface MyPageProps {
  onClose: () => void;
}

type MyPageMenu = "account" | "project" | "history";

const MODAL_WIDTH_PX = 840;
const MODAL_HEIGHT_PX = 654;
const NAV_WIDTH_PX = 180;
/** 바깥 Dialog 의 `aria-labelledby` 가 가리키는 제목 id. */
const TITLE_ID = "side-profile-modal";

const MENU_ITEMS: { id: MyPageMenu; icon: LineGlyphName; labelKey: `myPage.menu.${"accountInfo" | "projectPermission" | "projectHistory"}` }[] = [
  { id: "account", icon: "user", labelKey: "myPage.menu.accountInfo" },
  { id: "project", icon: "users", labelKey: "myPage.menu.projectPermission" },
  { id: "history", icon: "clock", labelKey: "myPage.menu.projectHistory" },
];

export const MyPage: FC<MyPageProps> = ({ onClose }) => {
  const { t } = useTranslation("projects");
  const { signOut } = useAuth();
  const [activeMenu, setActiveMenu] = useState<MyPageMenu>("account");
  const [logoutOpen, setLogoutOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);

  // signOut 은 실패를 안에서 처리하고 던지지 않는다 — 끝나면 성공(화면 이동)·실패 모두 창을 닫는다.
  const confirmLogout = async () => {
    if (signingOut) return;
    setSigningOut(true);
    await signOut();
    setSigningOut(false);
    setLogoutOpen(false);
  };

  return (
    <Box
      sx={{
        width: MODAL_WIDTH_PX,
        maxWidth: "100%",
        height: MODAL_HEIGHT_PX,
        display: "flex",
        flexDirection: "column",
        backgroundColor: COLOR_CONSTANTS.common.white_states.main,
        overflow: "hidden",
      }}
    >
      <ModalHeader
        title={t("myPage.title", { ns: "projects" })}
        titleId={TITLE_ID}
        titleComponent="h2"
        size="small"
        onClose={onClose}
        // 공용 small 헤더 제목(16px·500)은 회색 메뉴 바탕 옆에서 흐려 보여 이 모달에서만 굵게 둔다.
        sx={{ flexShrink: 0, [`& #${TITLE_ID}`]: { fontWeight: 700, color: COLOR_CONSTANTS.text.primary } }}
      />
      <Box sx={{ flex: 1, minHeight: 0, display: "flex" }}>
        <Box
          component="nav"
          aria-label={t("myPageModal.menuLabel", { ns: "projects" })}
          sx={{
            width: NAV_WIDTH_PX,
            flexShrink: 0,
            padding: "12px 8px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            backgroundColor: MYPAGE_NAV_BG,
            borderRight: "1px solid rgba(0, 0, 0, 0.06)",
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: "2px" }}>
            {MENU_ITEMS.map((item) => (
              <SideMenuItem
                key={item.id}
                icon={item.icon}
                label={t(item.labelKey, { ns: "projects" })}
                isActive={activeMenu === item.id}
                onSelect={() => setActiveMenu(item.id)}
              />
            ))}
          </Box>
          <SideMenuItem icon="logout" label={t("myPage.logout", { ns: "projects" })} onSelect={() => setLogoutOpen(true)} />
        </Box>

        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            backgroundColor: MYPAGE_SURFACE_BG,
          }}
        >
          <BranchFade key={activeMenu}>
            {activeMenu === "account" && <AccountInfo />}
            {activeMenu === "project" && <UserManagement />}
            {activeMenu === "history" && <ProjectHistory />}
          </BranchFade>
        </Box>
      </Box>
      <LogoutConfirmDialog
        open={logoutOpen}
        signingOut={signingOut}
        onCancel={() => setLogoutOpen(false)}
        onConfirm={confirmLogout}
      />
    </Box>
  );
};
